import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Brush, Loader2, Send, X, Wrench } from 'lucide-react';

import { IconButton } from '@/components/shared';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useEmoiChatStore } from '@/stores/emoiChatStore';
import { useEmoiSend } from '@/hooks/emoi/useEmoiSend';
import { useRouteContext } from '@/hooks/emoi/useRouteContext';

export function EmoiPanel() {
  const { t } = useTranslation();
  const isOpen = useEmoiChatStore((s) => s.isOpen);
  const close = useEmoiChatStore((s) => s.close);
  const messages = useEmoiChatStore((s) => s.messages);
  const isSending = useEmoiChatStore((s) => s.isSending);
  const { send, clear } = useEmoiSend();
  const routeContext = useRouteContext();

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isOpen]);

  const submit = async () => {
    if (!draft.trim() || isSending) return;
    const text = draft;
    setDraft('');
    await send(text);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <>
      <div
        className={`fixed top-14 left-0 right-0 bottom-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-200 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={close}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={t('emoi.title')}
        className={[
          'fixed z-50 bg-card border-l border-border shadow-xl flex flex-col',
          'top-14 bottom-0 right-0 w-full md:w-[440px] max-w-full',
          'transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
      >
        <header className="flex-none px-4 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="vess-iridescent-text text-base font-semibold lowercase">
              {t('emoi.title')}.
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {t('emoi.subtitle')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              icon={Brush}
              label={t('emoi.clear')}
              onClick={clear}
              disabled={isSending || messages.length === 0}
            />
            <IconButton icon={X} label={t('emoi.close')} onClick={close} />
          </div>
        </header>

        {routeContext.page !== 'unknown' && (
          <div className="flex-none px-4 py-2 border-b border-border bg-muted/20 text-[11px] text-muted-foreground">
            <span className="uppercase tracking-wide opacity-70">{t('emoi.contextLabel')}</span>
            <span className="mx-2">·</span>
            <span className="font-mono">{routeContext.page}</span>
            {Object.keys(routeContext.entityIds).length > 0 && (
              <span className="ml-2 opacity-70">
                ({Object.keys(routeContext.entityIds).join(', ')})
              </span>
            )}
          </div>
        )}

        <ScrollArea className="flex-1 min-h-0">
          <div ref={scrollRef} className="p-4 space-y-3">
            {messages.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('emoi.empty')}
              </p>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} role={m.role} content={m.content} pending={m.pending} receipts={m.toolReceipts} />
            ))}
          </div>
        </ScrollArea>

        <footer className="flex-none p-3 border-t border-border">
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('emoi.placeholder')}
              rows={2}
              className="resize-none min-h-[44px] max-h-32"
              disabled={isSending}
            />
            <IconButton
              icon={Send}
              label={t('emoi.send')}
              onClick={submit}
              isLoading={isSending}
              disabled={!draft.trim()}
              className="h-10 w-10 bg-primary text-primary-foreground hover:bg-primary/90"
            />
          </div>
        </footer>
      </aside>
    </>
  );
}

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
  receipts?: Array<{ name: string; text: string; success: boolean }>;
}

function MessageBubble({ role, content, pending, receipts }: MessageBubbleProps) {
  const { t } = useTranslation();
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={[
          'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground border border-border',
        ].join(' ')}
      >
        {receipts && receipts.length > 0 && (
          <div className="mb-2 space-y-1">
            {receipts.map((r, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 text-[11px] rounded border px-2 py-1 ${
                  r.success
                    ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300'
                    : 'border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-300'
                }`}
              >
                <Wrench className="h-3 w-3 mt-0.5 flex-none" />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] opacity-80">{t('emoi.toolCall')} · {r.name}</div>
                  <div className="break-words">{r.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {pending ? (
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('emoi.pending')}
          </span>
        ) : (
          content || ' '
        )}
      </div>
    </div>
  );
}
